import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useApiMutation } from '@/hooks/useApiMutation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pencil, Trash2, Plus, Percent, Search } from 'lucide-react';

interface YieldRow {
  _id: string;
  ingredient: string;
  canonical: string;
  yieldPercent: number;
}

const EMPTY: Omit<YieldRow, '_id'> = { ingredient: '', canonical: '', yieldPercent: 100 };

function YieldForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial: Omit<YieldRow, '_id'>;
  onSave: (data: Omit<YieldRow, '_id'>) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState(initial);

  const handleSave = () => {
    // Canonical is the internal lookup key — not admin-editable, always derived from
    // the display name so it stays in sync and admins don't need to understand it.
    onSave({ ...form, canonical: form.ingredient.toLowerCase().trim() });
  };

  const isValid = form.ingredient.trim() !== '' && form.yieldPercent > 0 && form.yieldPercent <= 100;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label>Display name</Label>
        <Input value={form.ingredient} onChange={e => setForm(p => ({ ...p, ingredient: e.target.value }))} placeholder="e.g. Whole papaya" />
      </div>
      <div className="space-y-1">
        <Label>Yield %</Label>
        <Input
          type="number"
          min="1"
          max="100"
          step="1"
          value={form.yieldPercent ?? ''}
          onChange={e => setForm(p => ({ ...p, yieldPercent: e.target.value === '' ? 0 : parseFloat(e.target.value) }))}
          placeholder="e.g. 65"
        />
        <p className="text-xs text-muted-foreground">% of as-purchased weight usable after peeling/trimming/seeding.</p>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving || !isValid}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

export default function YieldReferences() {
  const [editingRow, setEditingRow] = useState<YieldRow | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery<{ rows: YieldRow[] }>({
    queryKey: ['/api/admin/yield-references'],
    queryFn: () => apiRequest('GET', '/api/admin/yield-references') as unknown as Promise<{ rows: YieldRow[] }>,
  });

  const rows = data?.rows ?? [];
  const q = search.trim().toLowerCase();
  const filteredRows = q
    ? rows.filter(r => r.ingredient.toLowerCase().includes(q) || r.canonical.toLowerCase().includes(q))
    : rows;

  const createMutation = useApiMutation({
    endpoint: () => '/api/admin/yield-references',
    method: 'POST',
    invalidate: [['/api/admin/yield-references']],
    successMessage: 'Row added',
    errorMessage: 'Failed to add row',
  });

  const updateMutation = useApiMutation({
    endpoint: (vars: { id: string }) => `/api/admin/yield-references/${vars.id}`,
    method: 'PATCH',
    invalidate: [['/api/admin/yield-references']],
    successMessage: 'Row updated',
    errorMessage: 'Failed to update row',
  });

  const deleteMutation = useApiMutation({
    endpoint: (vars: { id: string }) => `/api/admin/yield-references/${vars.id}`,
    method: 'DELETE',
    invalidate: [['/api/admin/yield-references']],
    successMessage: 'Row deleted',
    errorMessage: 'Failed to delete row',
  });

  const handleAdd = (form: Omit<YieldRow, '_id'>) => {
    createMutation.mutate(form as any, { onSuccess: () => setAddOpen(false) });
  };

  const handleUpdate = (form: Omit<YieldRow, '_id'>) => {
    if (!editingRow) return;
    updateMutation.mutate({ id: editingRow._id, ...form } as any, { onSuccess: () => setEditingRow(null) });
  };

  const handleDelete = (id: string) => {
    if (!confirm('Delete this entry?')) return;
    deleteMutation.mutate({ id } as any);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Yield % References</h1>
          <p className="text-muted-foreground">Standard usable-weight percentages auto-filled when a matching ingredient is created in Pantry → Ingredients</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> Add Entry
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 space-y-0">
          <div>
            <div className="flex items-center gap-2">
              <Percent className="w-5 h-5" />
              <CardTitle>Yield Table</CardTitle>
            </div>
            <CardDescription>
              {q ? `${filteredRows.length} of ${rows.length} entries` : `${rows.length} entries`} · sorted alphabetically by canonical name
            </CardDescription>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search ingredients…"
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <p className="p-6 text-sm text-muted-foreground">Loading…</p>}
          {!isLoading && rows.length === 0 && (
            <p className="p-6 text-sm text-muted-foreground">No entries yet. Run the seed script or add entries manually.</p>
          )}
          {!isLoading && rows.length > 0 && filteredRows.length === 0 && (
            <p className="p-6 text-sm text-muted-foreground">No entries match "{search}".</p>
          )}
          {filteredRows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground text-xs uppercase tracking-wide bg-muted/30">
                    <th className="text-left px-4 py-2">Display name</th>
                    <th className="text-left px-4 py-2">Canonical</th>
                    <th className="text-right px-4 py-2">Yield %</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredRows.map(row => (
                    <tr key={row._id} className="hover:bg-muted/20">
                      <td className="px-4 py-2 capitalize">{row.ingredient}</td>
                      <td className="px-4 py-2 text-muted-foreground">{row.canonical}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.yieldPercent}%</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingRow(row)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDelete(row._id)}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Add Yield % Entry</DialogTitle></DialogHeader>
          <YieldForm initial={EMPTY} onSave={handleAdd} onCancel={() => setAddOpen(false)} saving={createMutation.isPending} />
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editingRow} onOpenChange={open => !open && setEditingRow(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Edit Yield % Entry</DialogTitle></DialogHeader>
          {editingRow && (
            <YieldForm
              initial={{ ingredient: editingRow.ingredient, canonical: editingRow.canonical, yieldPercent: editingRow.yieldPercent }}
              onSave={handleUpdate}
              onCancel={() => setEditingRow(null)}
              saving={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
